import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Button,
  TextField,
  IconButton,
  Switch,
  FormControlLabel,
  Divider,
  Grid,
  Container,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { getAuth, updateProfile, updateEmail } from 'firebase/auth';
import { db } from '../../config/firebase';
import type { User } from '../../types/auth';
import { AuthMethodsManager } from '../../components/auth/AuthMethodsManager';
import { Link as RouterLink } from 'react-router-dom';
// Admin emails are now loaded dynamically from Firestore
import { useAdminEmails } from '../../hooks/useAdminEmails';

const auth = getAuth();

export const Profile: React.FC = () => {
  const { adminEmails } = useAdminEmails();
  const { state, dispatch } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editedUser, setEditedUser] = useState<User | null>(() => {
    if (!state.user) return null;
    
    return {
      ...state.user,
      preferences: {
        theme: state.user.preferences?.theme || 'system',
        notifications: {
          sms: state.user.preferences?.notifications?.sms ?? false,
          push: state.user.preferences?.notifications?.push ?? false,
          trades: state.user.preferences?.notifications?.trades ?? false,
          performance: state.user.preferences?.notifications?.performance ?? false
        },
        apiKeys: state.user.preferences?.apiKeys || {}
      }
    };
  });

  if (!state.user || !editedUser) {
    return <Typography>Loading...</Typography>;
  }

  const handleNotificationChange = (type: keyof User['preferences']['notifications']) => 
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!editedUser) return;
      
      setEditedUser({
        ...editedUser,
        preferences: {
          ...editedUser.preferences,
          notifications: {
            ...editedUser.preferences.notifications,
            [type]: event.target.checked
          }
        }
      });
    };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditedUser(state.user ? {
      ...state.user,
      preferences: {
        theme: state.user.preferences?.theme || 'system',
        notifications: {
          sms: state.user.preferences?.notifications?.sms ?? false,
          push: state.user.preferences?.notifications?.push ?? false,
          trades: state.user.preferences?.notifications?.trades ?? false,
          performance: state.user.preferences?.notifications?.performance ?? false
        },
        apiKeys: state.user.preferences?.apiKeys || {}
      }
    } : null);
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!editedUser) return;

    try {
      // Use email as the document ID since that's how we store it
      const userRef = doc(db, 'users', editedUser.email);
      
      // Create update object without undefined values
      const updateData = {
        name: editedUser.name || '',
        phoneNumber: editedUser.phoneNumber || '',
        preferences: {
          theme: editedUser.preferences?.theme || 'system',
          notifications: {
            sms: editedUser.preferences?.notifications?.sms ?? false,
            push: editedUser.preferences?.notifications?.push ?? false,
            trades: editedUser.preferences?.notifications?.trades ?? false,
            performance: editedUser.preferences?.notifications?.performance ?? false
          },
          apiKeys: editedUser.preferences?.apiKeys || {}
        }
      };
      
      // First update Firestore
      await updateDoc(userRef, updateData);

      // Then update Firebase Auth profile
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, {
          displayName: editedUser.name || '',
          photoURL: editedUser.photoURL || null
        });

        // If email changed, update it separately (requires recent authentication)
        if (auth.currentUser.email !== editedUser.email) {
          try {
            // Note: This might fail if user hasn't recently signed in
            // In that case, we should prompt for re-authentication
            await updateEmail(auth.currentUser, editedUser.email);
          } catch (error: any) {
            if (error.code === 'auth/requires-recent-login') {
              // Reset the email back to original
              setEditedUser(prev => prev ? {
                ...prev,
                email: auth.currentUser?.email || prev.email
              } : null);
              throw new Error('Please sign out and sign in again to change your email address');
            }
            throw error;
          }
        }
      }

      dispatch({ type: 'SET_USER', payload: editedUser });
      setIsEditing(false);
    } catch (error: any) {
      console.error('Error updating profile:', error);
      // Show error to user
      alert(error.message || 'Failed to update profile. Please try again.');
    }
  };

  const handleApiKeyChange = (provider: 'alpaca' | 'schwab' | 'tdameritrade', field: string, value: string) => {
    setEditedUser({
      ...editedUser,
      preferences: {
        ...editedUser.preferences,
        apiKeys: {
          ...editedUser.preferences?.apiKeys,
          [provider]: {
            ...(editedUser.preferences?.apiKeys?.[provider] || {}),
            [field]: value
          }
        }
      }
    });
  };

  return (
    <Container 
      maxWidth={false}
      sx={{ 
        height: '100vh',
        overflow: 'auto',
        py: 3,
        px: { xs: 2, sm: 3 }
      }}
    >
      <Grid container spacing={3} sx={{ maxWidth: 1400, mx: 'auto' }}>
        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={3}>
                <Avatar
                  sx={{ width: 100, height: 100, mb: 2 }}
                  src={editedUser.photoURL || ''}
                  alt={editedUser.name}
                />
                <Box flex={1}>
                  {isEditing ? (
                    <>
                      <TextField
                        fullWidth
                        label="Name"
                        variant="outlined"
                        value={editedUser.name}
                        onChange={(e) => setEditedUser({ ...editedUser, name: e.target.value })}
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        fullWidth
                        label="Email"
                        value={editedUser.email}
                        onChange={(e) => setEditedUser({ ...editedUser, email: e.target.value })}
                        sx={{ mb: 2 }}
                        type="email"
                      />
                      <TextField
                        fullWidth
                        label="Phone Number"
                        value={editedUser.phoneNumber}
                        onChange={(e) => setEditedUser({ ...editedUser, phoneNumber: e.target.value })}
                        sx={{ mb: 2 }}
                        type="tel"
                      />
                    </>
                  ) : (
                    <>
                      <Typography variant="h5" gutterBottom>{editedUser.name}</Typography>
                      <Typography color="textSecondary" gutterBottom>Email: {editedUser.email}</Typography>
                      {editedUser.phoneNumber && (
                        <Typography color="textSecondary" gutterBottom>
                          Phone: {editedUser.phoneNumber}
                        </Typography>
                      )}
                      <Typography color="textSecondary" gutterBottom>
                        Member since: {new Date(editedUser.createdAt).toLocaleDateString()}
                      </Typography>
                      <Typography color="textSecondary">
                        Last login: {new Date(editedUser.lastLoginAt).toLocaleDateString()}
                      </Typography>
                    </>
                  )}
                </Box>
                <Box>
                  {isEditing ? (
                    <>
                      <IconButton onClick={handleSave} color="primary" title="Save">
                        <SaveIcon />
                      </IconButton>
                      <IconButton onClick={handleCancel} color="default" title="Cancel">
                        <CancelIcon />
                      </IconButton>
                    </>
                  ) : (
                    <IconButton onClick={handleEdit} color="primary" title="Edit">
                      <EditIcon />
                    </IconButton>
                  )}
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box>
                <Typography variant="h6" gutterBottom>
                  Notification Preferences
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editedUser.preferences.notifications.sms}
                      onChange={handleNotificationChange('sms')}
                      disabled={!isEditing}
                    />
                  }
                  label="SMS Notifications"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={editedUser.preferences.notifications.push}
                      onChange={handleNotificationChange('push')}
                      disabled={!isEditing}
                    />
                  }
                  label="Push Notifications"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={editedUser.preferences.notifications.trades}
                      onChange={handleNotificationChange('trades')}
                      disabled={!isEditing}
                    />
                  }
                  label="Trade Alerts"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={editedUser.preferences.notifications.performance}
                      onChange={handleNotificationChange('performance')}
                      disabled={!isEditing}
                    />
                  }
                  label="Performance Updates"
                />
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* API Keys Section */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  Trading API Keys
                </Typography>
                
                {/* Alpaca API Keys */}
                <Box mb={3}>
                  <Typography variant="subtitle1" gutterBottom>Alpaca API Keys</Typography>
                  {isEditing ? (
                    <>
                      <TextField
                        fullWidth
                        label="Alpaca API Key"
                        value={editedUser.preferences?.apiKeys?.alpaca?.key || ''}
                        onChange={(e) => handleApiKeyChange('alpaca', 'key', e.target.value)}
                        sx={{ mb: 2 }}
                        type="password"
                      />
                      <TextField
                        fullWidth
                        label="Alpaca API Secret"
                        value={editedUser.preferences?.apiKeys?.alpaca?.secret || ''}
                        onChange={(e) => handleApiKeyChange('alpaca', 'secret', e.target.value)}
                        sx={{ mb: 2 }}
                        type="password"
                      />
                    </>
                  ) : (
                    <Typography color="textSecondary">
                      {editedUser.preferences?.apiKeys?.alpaca?.key ? '••••••••' : 'Not configured'}
                    </Typography>
                  )}
                </Box>

                {/* Charles Schwab API Keys */}
                <Box mb={3}>
                  <Typography variant="subtitle1" gutterBottom>Charles Schwab API Keys</Typography>
                  {isEditing ? (
                    <>
                      <TextField
                        fullWidth
                        label="Schwab Client ID"
                        value={editedUser.preferences?.apiKeys?.schwab?.clientId || ''}
                        onChange={(e) => handleApiKeyChange('schwab', 'clientId', e.target.value)}
                        sx={{ mb: 2 }}
                        type="password"
                      />
                      <TextField
                        fullWidth
                        label="Schwab Client Secret"
                        value={editedUser.preferences?.apiKeys?.schwab?.clientSecret || ''}
                        onChange={(e) => handleApiKeyChange('schwab', 'clientSecret', e.target.value)}
                        sx={{ mb: 2 }}
                        type="password"
                      />
                    </>
                  ) : (
                    <Typography color="textSecondary">
                      {editedUser.preferences?.apiKeys?.schwab?.clientId ? '••••••••' : 'Not configured'}
                    </Typography>
                  )}
                </Box>

                {/* TD Ameritrade API Keys */}
                <Box>
                  <Typography variant="subtitle1" gutterBottom>TD Ameritrade API Keys</Typography>
                  {isEditing ? (
                    <>
                      <TextField
                        fullWidth
                        label="TD Ameritrade API Key"
                        value={editedUser.preferences?.apiKeys?.tdameritrade?.apiKey || ''}
                        onChange={(e) => handleApiKeyChange('tdameritrade', 'apiKey', e.target.value)}
                        sx={{ mb: 2 }}
                        type="password"
                      />
                      <TextField
                        fullWidth
                        label="TD Ameritrade Account ID"
                        value={editedUser.preferences?.apiKeys?.tdameritrade?.accountId || ''}
                        onChange={(e) => handleApiKeyChange('tdameritrade', 'accountId', e.target.value)}
                        sx={{ mb: 2 }}
                      />
                    </>
                  ) : (
                    <Typography color="textSecondary">
                      {editedUser.preferences?.apiKeys?.tdameritrade?.apiKey ? '••••••••' : 'Not configured'}
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Security Settings Card */}
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom sx={{ color: 'white', mb: 3 }}>
                Security Settings
              </Typography>
              <AuthMethodsManager />
            </CardContent>
          </Card>
        </Grid>

        {/* Admin Tools Card */}
        {adminEmails.includes(state.user?.email) && (
          <Grid item xs={12} md={5}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                  Admin Tools
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  These pages display completely unmodified raw data directly from the exchange APIs, 
                  showing exactly what's coming from the APIs without any transformations.
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom>
                  Raw API Data Access
                </Typography>
                <List>
                  <ListItem component={RouterLink} to="/admin-kraken" sx={{ cursor: 'pointer' }}>
                    <ListItemIcon>
                      <VisibilityIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Kraken Admin Dashboard" 
                      secondary="View completely unmodified raw data from Kraken API"
                    />
                  </ListItem>
                  <ListItem component={RouterLink} to="/admin-binanceus" sx={{ cursor: 'pointer' }}>
                    <ListItemIcon>
                      <VisibilityIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Binance US Admin Dashboard" 
                      secondary="View completely unmodified raw data from Binance US API"
                    />
                  </ListItem>
                  <ListItem component={RouterLink} to="/admin-coinbase" sx={{ cursor: 'pointer' }}>
                    <ListItemIcon>
                      <VisibilityIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Coinbase Admin Dashboard" 
                      secondary="View completely unmodified raw data from Coinbase API"
                    />
                  </ListItem>
                  <ListItem component={RouterLink} to="/admin-uniswap" sx={{ cursor: 'pointer' }}>
                    <ListItemIcon>
                      <VisibilityIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Uniswap Admin Dashboard" 
                      secondary="View completely unmodified raw data from Uniswap API"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};
